import React from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import VatCheckField from './VatCheckField';
import {fmt, fmtDate, isoWeek} from './format';

// Beleg-Kopf: erste Zeile, drei Stammdaten-Kacheln und die verknüpften Belege
// (Original `renderInfo` 2079 mit `orderFacts` 2054 und `renderCustomerInfo`
// 2769). Beschriftungen wörtlich aus dem Original.

/** Ortsmarke vor „Belegadresse" (Original ICO.pin). */
function PinIcon() {
    return (
        <svg className="ico ico-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z"/>
            <circle cx="12" cy="10" r="2.6"/>
        </svg>
    );
}

/** Eine Zeile innerhalb einer Stammdaten-Kachel. */
function Fact({label, children, help}) {
    const empty = children === null || children === undefined || children === '';

    return (
        <div className="of-row">
            <span className="of-l">
                {label}
                {help ? <HelpTooltip id={`oe-fact-${label}`} placement="top" size={0.7}>{help}</HelpTooltip> : null}
            </span>
            <span className="of-v">{empty ? <span className="muted">—</span> : children}</span>
        </div>
    );
}

export default function DocumentHeader({
    mode,
    order,
    weclappBaseUrl,
    vatResults,
    onVatResult,
    onOpenPdf,
    onOpenDoc,
    newDocLinks,
    onTerminate,
    terminateLabel,
    canTerminate,
    onCancelShipment,
    onOpenPrintMask,
}) {
    if (!order) {
        return null;
    }

    const p = order.payment || {};
    const a = order.recordAddress || {};
    const hasPrice = mode === 'order';
    const webUrl = order.webUrl || (weclappBaseUrl && order.orderId
        ? `${weclappBaseUrl}/webapp/view/order/SalesOrderDetail.page?entityId=${order.orderId}`
        : null);

    const addressLines = [
        a.company || order.customerName || '',
        a.street1 || '',
        [a.zipCode || a.zipcode, a.city].filter(Boolean).join(' '),
        a.countryCode || '',
    ].filter(Boolean);

    const shipments = order.shipments || [];
    // Gerade erzeugte Belege ploppen kurz auf, damit der Bearbeiter sieht, was
    // aus seiner Aktion entstanden ist (Original NEW_DOCLINKS).
    const frisch = new Set(newDocLinks || []);

    return (
        <div className="info show">
            <div>
                <b>{order.number}</b> · Kunde {order.customerNumber} {order.customerName || ''}
                {mode === 'order' && order.shopNumber ? (
                    <>
                        {' · '}
                        <OeTip tip="Nummer der Bestellung im Onlineshop (Zusatzfeld in weclapp).">
                            <span className="badge" style={{background: 'var(--acc)', color: '#fff'}}>
                                🛒 Shop-Nr {order.shopNumber}
                            </span>
                        </OeTip>
                    </>
                ) : null}
                {' · '}{(order.items || []).length} Positionen
                {hasPrice ? ` · Netto ${fmt(order.netAmount)} ${order.currency || ''}` : ''}
                {mode === 'shipment' && order.warehouseName ? (
                    <> · <span className="muted">ab Lager</span> <b>{order.warehouseName}</b></>
                ) : null}
                {' '}
                <OeTip tip={order.editable
                    ? 'Der Beleg lässt sich bearbeiten: Mengen, Rabatte und die Lagerwahl je Position sind hier änderbar.'
                    : `Der Beleg steht auf Status ${order.status} und ist damit in weclapp gesperrt.`}>
                    <span className={`badge ${order.editable ? 'ok' : 'no'}`}>
                        {order.editable ? 'editierbar' : `Status ${order.status} – gesperrt`}
                    </span>
                </OeTip>
                {webUrl ? (
                    <>
                        {'  ·  '}
                        <OeTip tip="Öffnet denselben Beleg in weclapp in einem neuen Browser-Tab.">
                            <a className="wl" href={webUrl} target="_blank" rel="noopener noreferrer">in weclapp öffnen ↗</a>
                        </OeTip>
                    </>
                ) : null}
                <OeTip tip="Öffnet die am Beleg hängenden PDF-Dateien (Auftragsbestätigung, Lieferscheine, Sammelbeleg) zum Ansehen, Drucken und Herunterladen.">
                    <button
                        type="button"
                        className="actbtn"
                        style={{padding: '2px 10px', fontSize: 12.5, marginLeft: 10}}
                        onClick={onOpenPdf}
                    >
                        📄 Belege / PDF
                    </button>
                </OeTip>
                {mode === 'order' && onOpenPrintMask ? (
                    <OeTip
                        tip="Druckt Deckblatt, Bestellung und Kommissionierschein als EIN kompaktes PDF ohne Logo."
                        detail="Bemerkungen aus dem Kundenstamm und vom Käufer stehen auf dem Deckblatt. weclapp-Belege wie die AB lassen sich im selben Druckvorgang mitschicken."
                    >
                        <button
                            type="button"
                            className="actbtn"
                            style={{padding: '2px 10px', fontSize: 12.5, marginLeft: 6}}
                            onClick={onOpenPrintMask}
                        >
                            🖨 Druckmaske
                        </button>
                    </OeTip>
                ) : null}
                {/* Löschen bzw. Stornieren (Original `#terminateBtn`, 2079):
                    nur am Auftrag und nur, solange er nicht gesperrt ist. Die
                    Beschriftung hängt daran, ob schon Belege am Auftrag
                    hängen — entschieden wird trotzdem frisch im Server. */}
                {mode === 'order' && canTerminate && order.editable ? (
                    <OeTip
                        tip={terminateLabel === '🗑 Auftrag löschen'
                            ? 'Entfernt den Auftrag endgültig aus weclapp, sofern er noch löschbar ist. Nicht umkehrbar.'
                            : 'Storniert den Auftrag bzw. schließt ihn manuell; offene Lieferscheine werden dabei gelöscht. Nicht umkehrbar.'}
                        detail={(terminateLabel === '🗑 Auftrag löschen'
                            ? 'Ob der Auftrag noch löschbar ist, prüft der Server. '
                            : 'Was noch möglich ist (stornieren oder manuell schließen), prüft der Server. ')
                            + 'Es kommt vorher eine Rückfrage. Im Testmodus läuft das als Trockenlauf.'}
                    >
                        <button
                            type="button"
                            className="actbtn"
                            style={{padding: '2px 10px', fontSize: 12.5, marginLeft: 8, borderColor: 'var(--err)', color: 'var(--err)'}}
                            onClick={onTerminate}
                        >
                            {terminateLabel}
                        </button>
                    </OeTip>
                ) : null}
            </div>

            {mode === 'order' ? (
                <div className="ofacts">
                    <div className="ofbox">
                        <div className="of-h">
                            Kunde
                            <HelpTooltip id="oe-tile-kunde" placement="top" size={0.7}>
                                Stammdaten des Kunden aus weclapp — Nummer, Name, E-Mail und USt-ID.
                            </HelpTooltip>
                        </div>
                        <Fact label="Nummer" help="Kundennummer in weclapp.">{order.customerNumber || ''}</Fact>
                        <Fact label="Name">{order.customerName || ''}</Fact>
                        <Fact label="E-Mail">
                            {order.customerEmail
                                ? <a className="wl" href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a>
                                : ''}
                        </Fact>
                        <div className="of-row">
                            <span className="of-l">USt-ID</span>
                            <span className="of-v">
                                <VatCheckField
                                    vatId={p.vatId || ''}
                                    order={order}
                                    cache={vatResults}
                                    onResult={onVatResult}
                                />
                            </span>
                        </div>
                    </div>

                    <div className="ofbox">
                        <div className="of-h">
                            <PinIcon/> Belegadresse
                            <HelpTooltip id="oe-tile-adresse" placement="top" size={0.7}>
                                Die auf diesem Beleg hinterlegte Anschrift. Sie ist der Vergleichswert der USt-ID-Prüfung.
                            </HelpTooltip>
                        </div>
                        <div className="of-addr">
                            {addressLines.length
                                ? addressLines.map((l, i) => <React.Fragment key={i}>{l}<br/></React.Fragment>)
                                : <span className="muted">—</span>}
                        </div>
                    </div>

                    <div className="ofbox">
                        <div className="of-h">
                            Auftrag
                            <HelpTooltip id="oe-tile-auftrag" placement="top" size={0.7}>
                                Kopfdaten des Auftrags: Anlagedatum mit Kalenderwoche, Vertriebsweg, Zahlungsbedingung
                                und die Bestellnummer des Kunden.
                            </HelpTooltip>
                        </div>
                        <Fact label="Datum" help="Anlagedatum des Auftrags mit Kalenderwoche nach ISO 8601.">
                            {order.orderDate
                                ? <>{fmtDate(order.orderDate)} <span className="muted">KW {isoWeek(order.orderDate)}</span></>
                                : ''}
                        </Fact>
                        <Fact label="Vertriebsweg" help="Preisgruppe des Auftrags. Sie entscheidet mit, ob Staffelrabatte greifen.">
                            {order.channelLabel || ''}
                        </Fact>
                        <Fact label="Zahlungsbed." help="Zahlungsbedingung am Auftrag; ohne eigene Angabe die des Kundenstamms.">
                            {p.termName || p.customerTermName || ''}
                        </Fact>
                        <Fact label="Ihre Best-Nr." help="Bestellnummer des Kunden, wie sie auf dem Beleg erscheint.">
                            {order.customerOrderNumber || ''}
                        </Fact>
                    </div>
                </div>
            ) : null}

            {mode === 'order' ? (
                <div className="doclinks">
                    <span className="muted">Lieferscheine:</span>{' '}
                    {shipments.length
                        ? shipments.map((s) => (
                            <OeTip key={s.number} tip={`${s.open ? 'offen' : 'abgeschlossen'} – Lieferschein im Auftrags-Editor öffnen`}>
                                <button
                                    type="button"
                                    className={`doclink${frisch.has(s.number) ? ' new' : ''}`}
                                    onClick={() => onOpenDoc && onOpenDoc(s.number)}
                                >
                                    <span className={`dot ${s.open ? 'open' : 'done'}`}/>{s.number}
                                </button>
                            </OeTip>
                        ))
                        : <span className="muted">noch keiner</span>}
                </div>
            ) : (order.order && order.order.number ? (
                <div className="doclinks">
                    <span className="muted">gehört zu Auftrag:</span>{' '}
                    <OeTip tip="Auftrag im Auftrags-Editor öffnen.">
                        <button type="button" className="doclink" onClick={() => onOpenDoc && onOpenDoc(order.order.number)}>
                            {order.order.number} ↗
                        </button>
                    </OeTip>
                    {order.status !== 'DELIVERED' && onCancelShipment ? (
                        <OeTip tip="Storniert diesen Lieferschein. Die Positionen werden am Auftrag wieder offen, du kannst danach neu liefern. Es kommt vorher eine Rückfrage; im Testmodus läuft es als Trockenlauf.">
                            <button
                                type="button"
                                className="actbtn"
                                style={{padding: '2px 10px', fontSize: 12.5, borderColor: 'var(--err)', color: 'var(--err)'}}
                                onClick={onCancelShipment}
                            >
                                🗑 Lieferschein stornieren
                            </button>
                        </OeTip>
                    ) : null}
                </div>
            ) : null)}
        </div>
    );
}
